import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getSettings, saveSettings, getApiBase, setApiBase, isLocalMode, setLocalMode, saveLocalData, getStorage } from '@/utils/storage';
import { Settings as SettingsType, Todo } from '@/types';
import { ArrowLeft, Moon, Sun, Monitor, Download, Upload, Server, Bell, LayoutGrid, Database, Save, CheckCircle2, HardDrive, CloudDownload, Clock, RefreshCw, XCircle, ListChecks } from 'lucide-react';
import { exportData, importData } from '@/utils/dataTransfer';

const Settings = () => {
  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [apiBase, setApiBaseState] = useState(getApiBase());
  const [localMode, setLocalModeState] = useState(isLocalMode());
  const [activeTab, setActiveTab] = useState<'basic' | 'server' | 'feishu' | 'data'>('basic');
  const [serverStatus, setServerStatus] = useState<'checking' | 'connected' | 'error' | 'idle'>('idle');
  const [showReminderLogs, setShowReminderLogs] = useState(false);
  const [reminderLogs, setReminderLogs] = useState<Array<{
    id: string;
    text: string;
    dueDate: number;
    triggerTime: number;
    reminderTiming: string;
    status: '待提醒' | '已提醒' | '未提醒';
  }>>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadData();
    if (!localMode) {
        checkServerStatus();
    }
  }, [localMode]); // Reload data when mode changes

  const checkServerStatus = async () => {
      setServerStatus('checking');
      try {
          // Simply fetch the base data endpoint to see if it responds
          // Set a timeout to avoid waiting too long
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
          
          const res = await fetch(`${apiBase}/data`, { signal: controller.signal });
          clearTimeout(timeoutId);
          
          if (res.ok) {
              setServerStatus('connected');
          } else {
              setServerStatus('error');
          }
      } catch (e) {
          setServerStatus('error');
          console.error("Server check failed:", e);
      }
  };

  const loadData = async () => {
    try {
        const data = await getSettings();
        // 迁移旧版非法模板：{"type":"todo"...} 不是飞书自定义机器人的合法 payload（缺少 msg_type），会被直接拒绝
        if (data.feishuConfig?.messageFormat?.includes('"type":"todo"')) {
            data.feishuConfig.messageFormat = '{"msg_type":"text","content":{"text":"🔔 待办提醒\\n事项：{content}\\n时间：{time}"}}';
            await saveSettings(data);
        }
        setSettings(data);
    } catch (e) {
        console.error("Failed to load settings", e);
        setSettings({
            theme: 'system',
            searchEngine: 'google',
            quickDialItems: [],
            feishuConfig: { 
                enabled: false, 
                webhookUrl: 'https://www.feishu.cn/flow/api/trigger-webhook/23da9fed99a14624f1d3e2ed646c87af', 
                messageFormat: '{"msg_type":"text","content":{"text":"🔔 待办提醒\\n事项：{content}\\n时间：{time}"}}' 
            }
        } as SettingsType);
    }
  };

  const handleLocalModeChange = (enabled: boolean) => {
      setLocalMode(enabled);
      setLocalModeState(enabled);
      // Reload page to ensure all components (like Sidebar, etc.) re-fetch data from correct source
      window.location.reload();
  };

  const handleBackupRemoteToLocal = async () => {
      if (confirm('确定要将当前服务端的数据覆盖到本地存储吗？此操作不可撤销。')) {
          try {
              const data = await getStorage();
              saveLocalData(data);
              alert('服务端数据已成功备份到本地！切换到本地模式后即可使用。');
          } catch (e) {
              console.error("Backup failed", e);
              alert('备份失败，请确保服务端连接正常。');
          }
      }
  };

  const handleApiBaseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setApiBaseState(e.target.value);
  };

  const handleSaveApiBase = () => {
      try {
          new URL(apiBase); 
          setApiBase(apiBase);
          if (!localMode) {
              checkServerStatus();
          }
          alert('服务端地址已更新');
      } catch (e) {
          alert('请输入有效的 URL 地址');
      }
  };

  const handleFeishuConfigChange = async (key: string, value: any) => {
    if (!settings) return;
    const currentConfig = settings.feishuConfig || { 
        webhookUrl: 'https://www.feishu.cn/flow/api/trigger-webhook/23da9fed99a14624f1d3e2ed646c87af', 
        enabled: false, 
        messageFormat: '{"msg_type":"text","content":{"text":"🔔 待办提醒\\n事项：{content}\\n时间：{time}"}}' 
    };
    const newConfig = { ...currentConfig, [key]: value };
    const newSettings = { ...settings, feishuConfig: newConfig };
    setSettings(newSettings);
  };

  const saveFeishuSettings = async () => {
      if (!settings) return;
      await saveSettings(settings);
      alert('飞书配置已保存');
  };

  const handleThemeChange = async (theme: 'light' | 'dark' | 'system') => {
    if (!settings) return;
    const newSettings = { ...settings, theme };
    setSettings(newSettings);

    try {
      await saveSettings(newSettings);
    } catch (e) {
      console.error('Failed to save theme settings', e);
      alert('主题保存失败：无法连接存储服务，重启后将恢复为之前的设置。请检查服务是否启动或地址是否正确。');
    }
    
    if (theme === 'dark') {
        document.documentElement.classList.add('dark');
    } else if (theme === 'light') {
        document.documentElement.classList.remove('dark');
    } else {
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }
  };

  const handleImport = () => {
      fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
          await importData(file);
          loadData();
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatReminderTiming = (timing: Todo['reminderTiming']) => {
    if (!timing) return '无';
    if (typeof timing === 'number') return `提前${timing}分钟`;
    if (timing.endsWith('d')) return `提前${parseInt(timing, 10)}天`;
    if (timing.endsWith('h')) return `提前${parseInt(timing, 10)}小时`;
    if (timing.endsWith('m')) return `提前${parseInt(timing, 10)}分钟`;
    return String(timing);
  };

  const reminderOffsetMinutes = (timing: Todo['reminderTiming']) => {
    if (!timing) return 0;
    if (typeof timing === 'number') return timing;
    const match = timing.match(/^(\d+)([dhm])$/);
    if (!match) return 0;
    const value = parseInt(match[1], 10);
    const unit = match[2];
    if (unit === 'd') return value * 24 * 60;
    if (unit === 'h') return value * 60;
    return value;
  };

  const loadReminderLogs = async () => {
    setLogsLoading(true);
    try {
      const data = await getStorage();
      const todos = data.todos || [];
      const now = Date.now();
      const logs = todos
        .filter(todo => !todo.completed && todo.dueDate && todo.reminderTiming)
        .map(todo => {
          const offset = reminderOffsetMinutes(todo.reminderTiming);
          const triggerTime = (todo.dueDate as number) - offset * 60 * 1000;
          let status: '待提醒' | '已提醒' | '未提醒' = '待提醒';
          if (todo.reminderSent) {
            status = '已提醒';
          } else if (now >= triggerTime) {
            status = '未提醒';
          }
          return {
            id: todo.id,
            text: todo.text,
            dueDate: todo.dueDate as number,
            triggerTime,
            reminderTiming: formatReminderTiming(todo.reminderTiming),
            status
          };
        })
        .sort((a, b) => b.triggerTime - a.triggerTime);
      setReminderLogs(logs);
    } catch (e) {
      console.error('加载提醒日志失败', e);
      alert('加载提醒日志失败，请稍后重试');
    } finally {
      setLogsLoading(false);
    }
  };

  const openReminderLogs = async () => {
    setShowReminderLogs(true);
    await loadReminderLogs();
  };

  if (!settings && !apiBase) return null;

    const tabs = [
        { id: 'basic', label: '基础设置', icon: LayoutGrid },
        { id: 'feishu', label: '飞书助手', icon: Bell },
        { id: 'data', label: '数据备份', icon: Database },
    ];

    return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
            <Link to="/" className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors">
                <ArrowLeft size={24} className="text-slate-600 dark:text-slate-300" />
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">设置</h1>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
            {/* Sidebar Navigation */}
            <div className="w-full md:w-64 flex-shrink-0">
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                    <div className="p-2">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                                    activeTab === tab.id 
                                    ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400' 
                                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                }`}
                            >
                                <tab.icon size={18} />
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 min-w-0">
                {activeTab === 'basic' && settings && (
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
                        {/* Local Mode Toggle */}
                        <div className="p-6 border-b border-slate-100 dark:border-slate-700">
                            <h2 className="text-lg font-medium text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                                <HardDrive size={20} /> 数据存储模式
                            </h2>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-sm font-medium text-slate-900 dark:text-white">本地模式</div>
                                    <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                        开启后，所有数据（AI助手、收藏、待办）将仅存储在本地浏览器中，不再与服务端同步。
                                    </div>
                                    {!localMode && (
                                        <button 
                                            onClick={handleBackupRemoteToLocal}
                                            className="mt-3 text-xs flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:underline"
                                        >
                                            <CloudDownload size={14} /> 备份远程数据到本地
                                        </button>
                                    )}
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        className="sr-only peer"
                                        checked={localMode}
                                        onChange={(e) => handleLocalModeChange(e.target.checked)}
                                    />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                </label>
                            </div>
                        </div>

                        <div className="p-6 border-b border-slate-100 dark:border-slate-700">
                            <h2 className="text-lg font-medium text-slate-900 dark:text-white mb-4">外观主题</h2>
                            <div className="flex gap-4">
                                <button 
                                    onClick={() => handleThemeChange('light')}
                                    className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${settings.theme === 'light' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'}`}
                                >
                                    <Sun size={24} className={settings.theme === 'light' ? 'text-blue-500' : 'text-slate-500'} />
                                    <span className={`text-sm font-medium ${settings.theme === 'light' ? 'text-blue-600' : 'text-slate-600 dark:text-slate-400'}`}>浅色</span>
                                </button>
                                <button 
                                    onClick={() => handleThemeChange('dark')}
                                    className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${settings.theme === 'dark' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'}`}
                                >
                                    <Moon size={24} className={settings.theme === 'dark' ? 'text-blue-500' : 'text-slate-500'} />
                                    <span className={`text-sm font-medium ${settings.theme === 'dark' ? 'text-blue-600' : 'text-slate-600 dark:text-slate-400'}`}>深色</span>
                                </button>
                                <button 
                                    onClick={() => handleThemeChange('system')}
                                    className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${settings.theme === 'system' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'}`}
                                >
                                    <Monitor size={24} className={settings.theme === 'system' ? 'text-blue-500' : 'text-slate-500'} />
                                    <span className={`text-sm font-medium ${settings.theme === 'system' ? 'text-blue-600' : 'text-slate-600 dark:text-slate-400'}`}>跟随系统</span>
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            <h2 className="text-lg font-medium text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                                <Server size={20} /> 服务端连接
                            </h2>
                            {localMode ? (
                                <div className="space-y-4">
                                    <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg text-slate-500 dark:text-slate-400 text-sm flex items-start gap-2">
                                        <div className="mt-0.5"><HardDrive size={16} /></div>
                                        <div>
                                            <p className="font-medium text-slate-700 dark:text-slate-300 mb-1">本地模式已开启</p>
                                            <p>当前所有数据存储在本地浏览器中。尽管如此，您仍可以配置服务端地址，以便在本地模式下加载远程图片资源（如上传的图标）。</p>
                                        </div>
                                    </div>
                                    
                                    <div className="pt-2">
                                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-2 font-medium">
                                            资源服务器地址 (仅用于加载图片)
                                        </p>
                                        <div className="flex gap-4">
                                            <input 
                                                type="text" 
                                                value={apiBase}
                                                onChange={handleApiBaseChange}
                                                placeholder="例如: http://localhost:3014/api"
                                                className="flex-1 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            <button 
                                                onClick={handleSaveApiBase}
                                                className="px-6 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 font-medium transition-colors flex items-center gap-2"
                                            >
                                                <Save size={18} /> 更新
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between mb-4">
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                            配置后端服务的 API 地址，用于同步书签、标签及待办数据。
                                        </p>
                                        <div className="flex items-center gap-2">
                                            {serverStatus === 'checking' && <span className="flex items-center gap-1 text-xs text-blue-500"><RefreshCw size={12} className="animate-spin" /> 检查中...</span>}
                                            {serverStatus === 'connected' && <span className="flex items-center gap-1 text-xs text-green-500 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-md"><CheckCircle2 size={12} /> 已连接</span>}
                                            {serverStatus === 'error' && (
                                                <div className="flex items-center gap-1 text-xs text-red-500 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded-md" title="常见原因：防火墙未开放端口 / HTTPS协议不匹配 / 服务未启动">
                                                    <XCircle size={12} /> 
                                                    <span>连接失败</span>
                                                </div>
                                            )}
                                            <button onClick={checkServerStatus} className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 ml-2" title="重新检查">
                                                <RefreshCw size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex gap-4 mb-6">
                                        <input 
                                            type="text" 
                                            value={apiBase}
                                            onChange={handleApiBaseChange}
                                            placeholder="例如: https://webtags.market.livedoc.cn:3014/api"
                                            className={`flex-1 px-4 py-2 rounded-lg border bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${serverStatus === 'error' ? 'border-red-300 dark:border-red-700 focus:ring-red-500' : 'border-slate-200 dark:border-slate-600'}`}
                                        />
                                        <button 
                                            onClick={handleSaveApiBase}
                                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center gap-2"
                                        >
                                            <Save size={18} /> 保存
                                        </button>
                                    </div>
                                </>
                            )}
                            
                            <div className="pt-4 border-t border-slate-100 dark:border-slate-700">
                                <h3 className="text-sm font-medium text-slate-900 dark:text-white mb-2">网站截图服务</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                                    配置用于生成网站缩略图的 API 地址。
                                </p>
                                <div className="flex gap-4">
                                    <input 
                                        type="text" 
                                        value={settings.screenshotApiUrl || ''}
                                        onChange={(e) => {
                                            const newSettings = { ...settings, screenshotApiUrl: e.target.value };
                                            setSettings(newSettings);
                                        }}
                                        onBlur={() => saveSettings(settings)}
                                        placeholder="例如: https://html2image.market.livedoc.cn/screenshot?url="
                                        className="flex-1 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                    />
                                    <button 
                                        onClick={() => {
                                            const defaultUrl = 'https://html2image.market.livedoc.cn/screenshot?url=';
                                            const newSettings = { ...settings, screenshotApiUrl: defaultUrl };
                                            setSettings(newSettings);
                                            saveSettings(newSettings);
                                        }}
                                        className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 font-medium transition-colors whitespace-nowrap"
                                        title="使用默认配置"
                                    >
                                        默认
                                    </button>
                                    <button 
                                        onClick={() => saveSettings(settings)}
                                        className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 font-medium transition-colors"
                                    >
                                        保存
                                    </button>
                                </div>
                                <p className="text-xs text-slate-400 mt-2">
                                    系统会自动将目标网址追加到该 API 地址后面。
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'server' && (
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
                        <div className="p-6">
                            <h2 className="text-lg font-medium text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                                <Server size={20} /> 服务端连接
                            </h2>
                            {localMode ? (
                                <div className="space-y-4">
                                    <div className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg text-slate-500 dark:text-slate-400 text-sm flex items-start gap-2">
                                        <div className="mt-0.5"><HardDrive size={16} /></div>
                                        <div>
                                            <p className="font-medium text-slate-700 dark:text-slate-300 mb-1">本地模式已开启</p>
                                            <p>当前所有数据存储在本地浏览器中。尽管如此，您仍可以配置服务端地址，以便在本地模式下加载远程图片资源（如上传的图标）。</p>
                                        </div>
                                    </div>
                                    
                                    <div className="pt-2">
                                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-2 font-medium">
                                            资源服务器地址 (仅用于加载图片)
                                        </p>
                                        <div className="flex gap-4">
                                            <input 
                                                type="text" 
                                                value={apiBase}
                                                onChange={handleApiBaseChange}
                                                placeholder="例如: http://localhost:3014/api"
                                                className="flex-1 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            <button 
                                                onClick={handleSaveApiBase}
                                                className="px-6 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 font-medium transition-colors flex items-center gap-2"
                                            >
                                                <Save size={18} /> 更新
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                                        配置后端服务的 API 地址，用于同步书签、标签及待办数据。
                                    </p>
                                    <div className="flex gap-4">
                                        <input 
                                            type="text" 
                                            value={apiBase}
                                            onChange={handleApiBaseChange}
                                            placeholder="例如: http://localhost:3014/api"
                                            className="flex-1 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <button 
                                            onClick={handleSaveApiBase}
                                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center gap-2"
                                        >
                                            <Save size={18} /> 保存
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'feishu' && settings && (
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-lg font-medium text-slate-900 dark:text-white flex items-center gap-2">
                                        <Bell size={20} /> 飞书消息通知
                                    </h2>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                        配置飞书群机器人 Webhook，实现待办事项的实时提醒。
                                    </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        className="sr-only peer"
                                        checked={settings.feishuConfig?.enabled || false}
                                        onChange={(e) => {
                                            handleFeishuConfigChange('enabled', e.target.checked);
                                            const newSettings = { 
                                                ...settings, 
                                                feishuConfig: { 
                                                    ...(settings.feishuConfig || { 
                                                        webhookUrl: 'https://www.feishu.cn/flow/api/trigger-webhook/23da9fed99a14624f1d3e2ed646c87af', 
                                                        messageFormat: '{"msg_type":"text","content":{"text":"🔔 待办提醒\\n事项：{content}\\n时间：{time}"}}' 
                                                    }), 
                                                    enabled: e.target.checked 
                                                } 
                                            };
                                            saveSettings(newSettings);
                                        }}
                                    />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                </label>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        Webhook 地址
                                    </label>
                                    <input
                                        type="text"
                                        value={settings.feishuConfig?.webhookUrl || 'https://www.feishu.cn/flow/api/trigger-webhook/23da9fed99a14624f1d3e2ed646c87af'}
                                        onChange={(e) => handleFeishuConfigChange('webhookUrl', e.target.value)}
                                        placeholder="https://open.feishu.cn/open-apis/bot/v2/hook/..."
                                        className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        签名校验密钥（可选）
                                    </label>
                                    <div className="text-xs text-slate-500 mb-2">
                                        若机器人在「安全设置」中开启了「签名校验」，请粘贴密钥；未开启则留空。参考
                                        <a href="https://open.feishu.cn/document/client-docs/bot-v3/add-custom-bot?lang=zh-CN" target="_blank" rel="noreferrer" className="text-blue-500 hover:underline ml-1">官方文档</a>
                                    </div>
                                    <input
                                        type="password"
                                        value={settings.feishuConfig?.secret || ''}
                                        onChange={(e) => handleFeishuConfigChange('secret', e.target.value)}
                                        placeholder="签名校验密钥，形如 xxxxxxxxxxxxxxxx"
                                        className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        消息格式 (支持变量)
                                    </label>
                                    <div className="text-xs text-slate-500 mb-2">
                                        可用变量: <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">{'{content}'}</code> (待办内容), <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">{'{time}'}</code> (提醒时间)
                                    </div>
                                    <textarea 
                                        rows={4}
                                        value={settings.feishuConfig?.messageFormat || '{"msg_type":"text","content":{"text":"🔔 待办提醒\\n事项：{content}\\n时间：{time}"}}'}
                                        onChange={(e) => handleFeishuConfigChange('messageFormat', e.target.value)}
                                        placeholder="请输入消息模版..."
                                        className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                    />
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700">
                                    <button 
                                        onClick={openReminderLogs}
                                        className="px-4 py-2 text-slate-600 bg-slate-100 dark:bg-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg font-medium transition-colors flex items-center gap-2"
                                    >
                                        <ListChecks size={16} /> 查看提醒日志
                                    </button>
                                    <button 
                                        onClick={async () => {
                                            if (!settings.feishuConfig?.webhookUrl) {
                                                alert('请先填写 Webhook 地址');
                                                return;
                                            }
                                            
                                            if (localMode) {
                                                try {
                                                    // Local Mode: Delegate to background script
                                                    chrome.runtime.sendMessage({ 
                                                        type: 'TEST_FEISHU', 
                                                        data: settings.feishuConfig 
                                                    }, (response) => {
                                                        if (chrome.runtime.lastError) {
                                                            console.error('Runtime error:', chrome.runtime.lastError);
                                                            alert('发送失败: 无法连接到后台服务');
                                                            return;
                                                        }
                                                        if (response && response.success) {
                                                            alert('测试消息已发送，请在飞书查看');
                                                        } else {
                                                            alert(`发送失败: ${response?.error || '请检查 Webhook 地址'}`);
                                                        }
                                                    });
                                                } catch (e) {
                                                    console.error('Send message failed', e);
                                                    alert('发送失败，请确保插件后台运行正常');
                                                }
                                                return;
                                            }

                                            try {
                                                const res = await fetch(`${getApiBase()}/feishu/test`, {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify(settings.feishuConfig)
                                                });
                                                if (res.ok) {
                                                    alert('测试消息已发送，请在飞书查看');
                                                } else {
                                                    const err = await res.json().catch(() => null);
                                                    alert(`发送失败: ${err?.error || '请检查 Webhook 地址'}`);
                                                }
                                            } catch (e) {
                                                alert('请求失败，请确保服务端已启动');
                                            }
                                        }}
                                        className="px-4 py-2 text-blue-600 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg font-medium transition-colors"
                                    >
                                        发送测试
                                    </button>
                                    <button 
                                        onClick={saveFeishuSettings}
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center gap-2"
                                    >
                                        <Save size={18} /> 保存配置
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'data' && (
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-700">
                            <h2 className="text-lg font-medium text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                                <Database size={20} /> 数据备份与恢复
                            </h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                                您可以导出所有数据（包括收藏夹、标签、设置、待办事项）进行备份，或从备份文件恢复数据。
                            </p>
                            <div className="flex gap-4">
                                <input 
                                    type="file" 
                                    ref={fileInputRef} 
                                    onChange={handleFileChange} 
                                    className="hidden" 
                                    accept=".json" 
                                />
                                <button 
                                    onClick={handleImport}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-750 font-medium transition-colors"
                                >
                                    <Upload size={18} /> 导入备份
                                </button>
                                <button 
                                    onClick={exportData}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors shadow-sm"
                                >
                                    <Download size={18} /> 导出备份
                                </button>
                            </div>
                        </div>

                        {localMode && (
                            <div className="p-6">
                                <h3 className="text-sm font-medium text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                                    <Clock size={16} /> 自动备份设置
                                </h3>
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <div className="text-sm font-medium text-slate-700 dark:text-slate-300">开启自动备份</div>
                                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                            开启后，系统将定期自动导出数据文件（仅在本地模式下生效）。<br/>
                                            备份文件将保存到您浏览器的默认下载目录中。
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            className="sr-only peer"
                                            checked={settings.autoBackup?.enabled ?? true} // Default enabled for local mode safety
                                            onChange={(e) => {
                                                const newSettings = { 
                                                    ...settings, 
                                                    autoBackup: { 
                                                        enabled: e.target.checked,
                                                        interval: settings.autoBackup?.interval || 240
                                                    } 
                                                };
                                                setSettings(newSettings);
                                                saveSettings(newSettings);
                                            }}
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                    </label>
                                </div>
                                
                                {(settings.autoBackup?.enabled ?? true) && (
                                    <div className="flex items-center gap-4">
                                        <label className="text-sm text-slate-700 dark:text-slate-300">备份间隔</label>
                                        <select
                                            value={settings.autoBackup?.interval || 240}
                                            onChange={(e) => {
                                                const newSettings = { 
                                                    ...settings, 
                                                    autoBackup: { 
                                                        ...settings.autoBackup,
                                                        enabled: true,
                                                        interval: parseInt(e.target.value)
                                                    } 
                                                };
                                                setSettings(newSettings);
                                                saveSettings(newSettings);
                                            }}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value={10}>每 10 分钟</option>
                                            <option value={60}>每 1 小时</option>
                                            <option value={240}>每 4 小时</option>
                                            <option value={720}>每 12 小时</option>
                                            <option value={1440}>每 24 小时</option>
                                        </select>
                                    </div>
                                )}
                                
                                {settings.autoBackup?.lastBackupTime && (
                                    <div className="mt-2 text-xs text-slate-400">
                                        上次备份时间: {new Date(settings.autoBackup.lastBackupTime).toLocaleString()}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
        {showReminderLogs && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-5xl bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">飞书提醒日志</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    当前模式：{localMode ? '本地模式' : '云端模式'}（显示待提醒 / 未提醒 / 已提醒）
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={loadReminderLogs}
                    className="px-3 py-1.5 text-sm rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center gap-1.5"
                  >
                    <RefreshCw size={14} /> 刷新
                  </button>
                  <button
                    onClick={() => setShowReminderLogs(false)}
                    className="px-3 py-1.5 text-sm rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600"
                  >
                    关闭
                  </button>
                </div>
              </div>

              <div className="max-h-[65vh] overflow-auto">
                {logsLoading ? (
                  <div className="p-8 text-center text-slate-500 dark:text-slate-400">日志加载中...</div>
                ) : reminderLogs.length === 0 ? (
                  <div className="p-8 text-center text-slate-500 dark:text-slate-400">暂无需要提醒的任务记录</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-700/40 text-slate-600 dark:text-slate-300">
                      <tr>
                        <th className="text-left px-4 py-3">任务</th>
                        <th className="text-left px-4 py-3">提醒规则</th>
                        <th className="text-left px-4 py-3">触发时间</th>
                        <th className="text-left px-4 py-3">截止时间</th>
                        <th className="text-left px-4 py-3">状态</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reminderLogs.map(log => (
                        <tr key={log.id} className="border-t border-slate-100 dark:border-slate-700">
                          <td className="px-4 py-3 text-slate-800 dark:text-slate-100">{log.text}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{log.reminderTiming}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{new Date(log.triggerTime).toLocaleString()}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{new Date(log.dueDate).toLocaleString()}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                              log.status === '已提醒'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : log.status === '待提醒'
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            }`}>
                              {log.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
